import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function LateNightVancouverEats() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouvers-best-late-night-eats";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "late-night-eats": "Vancouver’s Late-Night Eateries",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    disclaimers: "Disclaimers",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // We reference official resources, local tourism, magazine articles, etc.
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism Board",
    },
    {
      url: "https://www.vanmag.com",
      label: "Vancouver Magazine – Late Night Eats Article",
    },
    {
      url: "https://www.tripadvisor.com/Restaurants-g154943-zfp8-Vancouver_British_Columbia.html",
      label: "TripAdvisor – Vancouver Late Night Restaurants",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "lucys",
      lat: 49.2609,
      lng: -123.1008,
      popupText: "Lucy’s Eastside Diner",
    },
    {
      id: "duffins",
      lat: 49.2330,
      lng: -123.0772,
      popupText: "Duffin’s Donuts",
    },
    {
      id: "breka",
      lat: 49.2835,
      lng: -123.1265,
      popupText: "Breka Bakery & Café (Downtown Bute St.)",
    },
    {
      id: "naam",
      lat: 49.2683,
      lng: -123.1681,
      popupText: "The Naam (Kitsilano)",
    },
    {
      id: "houseofdosas",
      lat: 49.2485,
      lng: -123.0768,
      popupText: "House of Dosas (Kingsway)",
    },
    {
      id: "japadog",
      lat: 49.2802,
      lng: -123.1180,
      popupText: "JAPADOG (Robson Street Storefront)",
    },
    {
      id: "joefortes",
      lat: 49.2850,
      lng: -123.1249,
      popupText: "Joe Fortes Seafood & Chop House",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver’s Best Late-Night Eats</title>
        <meta
          name="description"
          content="A thorough guide to Vancouver's best after-hours restaurants and eateries, covering classic diners, 24/7 spots, international cuisine, and more."
        />
        <meta
          name="keywords"
          content="Vancouver late-night eats, 24-hour diners, House of Dosas, Lucy’s Eastside Diner, Duffin's Donuts, Breka Bakery"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouvers-best-late-night-eats.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver’s Best Late-Night Eats"
        />
        <meta
          property="og:description"
          content="Explore where to satisfy your hunger after hours in Vancouver, from all-night diners to upscale spots open late."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouvers-best-late-night-eats.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouvers-best-late-night-eats.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver’s Best Late-Night Eats"
        />
        <meta
          name="twitter:description"
          content="Find the best after-hours food in Vancouver: from 24/7 joints, to vegetarian spots, to street eats."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouvers-best-late-night-eats.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A neon-lit late-night restaurant in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver might not be the city that never sleeps, but it certainly
              offers plenty for night owls seeking a delicious bite after dark.
              When the sun sets, a different culinary scene awakens – from
              sizzling street food carts to cozy 24/7 diners and even upscale
              restaurants extending their hours. After a long day of exploring
              or a late night out, finding great food well past normal dinner
              hours can turn a good trip into a memorable one. In this guide,
              we’ll explore why Vancouver’s late-night dining scene is worth
              staying up for, whether you’re craving comfort food, a sweet treat,
              or a gourmet dish.
            </p>

            {/* SECTION: Late-Night Eateries */}
            <h2 id="late-night-eats">Vancouver’s Late-Night Eateries</h2>
            <p>
              Below are some of Vancouver’s finest options for after-hours eats,
              ranging from cheap and cheerful to upscale.
            </p>

            {/* Lucy's Eastside Diner */}
            <h3 ref={(el) => setSpotRef("lucys", el)} style={{ scrollMarginTop: "80px" }}>
              Lucy’s Eastside Diner{" "}
              <Marker
                coordY={49.2609}
                coordX={-123.1008}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              A retro-inspired diner in the Mount Pleasant neighborhood, Lucy’s
              Eastside Diner is a go-to for comfort food at any hour. The
              1950s-style decor, all-day breakfast offerings, and 24-hour
              weekend schedule make it especially popular with the late-night
              crowd. Try their classic milkshakes, poutine, or burgers if you’re
              in need of hearty, budget-friendly fare. Expect it to be bustling
              after midnight on weekends.
            </p>

            {/* Duffin’s Donuts */}
            <h3 ref={(el) => setSpotRef("duffins", el)} style={{ scrollMarginTop: "80px" }}>
              Duffin’s Donuts{" "}
              <Marker
                coordY={49.2330}
                coordX={-123.0772}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              Part donut shop, part diner, and open 24 hours a day, Duffin’s
              Donuts in East Vancouver is a beloved local institution. Their
              massive menu spans fresh donuts, fried chicken, Vietnamese banh mi,
              and Mexican tortas. It’s affordable, quick, and conveniently
              satisfies a variety of late-night cravings under one roof.
            </p>

            {/* Breka Bakery & Café */}
            <h3 ref={(el) => setSpotRef("breka", el)} style={{ scrollMarginTop: "80px" }}>
              Breka Bakery & Café (Bute Street){" "}
              <Marker
                coordY={49.2835}
                coordX={-123.1265}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              Breka Bakery & Café operates several locations across Vancouver,
              all of which are open 24/7. Perfect for a late-night coffee,
              pastries, or sandwiches, it’s also a popular spot for students and
              workers looking for a quiet place to study. With freshly baked
              goods around the clock, it’s hard to resist stopping by for a
              donut, slice of cake, or savory panini no matter the time.
            </p>

            {/* The Naam */}
            <h3 ref={(el) => setSpotRef("naam", el)} style={{ scrollMarginTop: "80px" }}>
              The Naam (Kitsilano){" "}
              <Marker
                coordY={49.2683}
                coordX={-123.1681}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              Vancouver’s oldest vegetarian restaurant (established in 1968),
              The Naam was historically open 24/7, though recent schedules may
              vary. Even so, it remains an iconic late-night choice for
              vegetarians and vegans. Known for hearty veggie burgers, bowls, and
              miso gravy fries, it offers a laid-back bohemian vibe in the heart
              of Kitsilano.
            </p>

            {/* House of Dosas */}
            <h3 ref={(el) => setSpotRef("houseofdosas", el)} style={{ scrollMarginTop: "80px" }}>
              House of Dosas (Kingsway){" "}
              <Marker
                coordY={49.2485}
                coordX={-123.0768}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              For South Indian fare at 2 AM, look no further than House of Dosas.
              Renowned for a wide variety of dosas (giant crispy rice crepes
              filled with savory curries), this casual restaurant is open 24/7
              and draws in a diverse crowd after midnight. From spicy masala
              dosa to butter chicken or lamb curries, there’s something to suit
              every palate.
            </p>

            {/* JAPADOG */}
            <h3 ref={(el) => setSpotRef("japadog", el)} style={{ scrollMarginTop: "80px" }}>
              JAPADOG (Robson Street){" "}
              <Marker
                coordY={49.2802}
                coordX={-123.1180}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              Vancouver’s famous Japanese-fusion hot dog stand operates multiple
              carts and a storefront downtown. Some locations stay open late on
              weekends, delighting night owls with inventive toppings like nori
              seaweed, teriyaki sauce, and bonito flakes. The Terimayo Dog is
              their signature creation, perfect for a quick bite on your way
              home.
            </p>

            {/* Joe Fortes Seafood & Chop House */}
            <h3 ref={(el) => setSpotRef("joefortes", el)} style={{ scrollMarginTop: "80px" }}>
              Joe Fortes Seafood & Chop House{" "}
              <Marker
                coordY={49.2850}
                coordX={-123.1249}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h3>
            <p>
              For an upscale late-night experience, Joe Fortes in downtown
              Vancouver serves dinner until 11 PM, offering premium seafood,
              steaks, and an oyster bar. Though not a 2 AM spot, it’s an
              excellent choice for those seeking a more refined meal after
              standard dining hours. Reservations are recommended, and the
              atmosphere is a classic combination of lively bar and elegant
              dining.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Vancouver’s liquor laws require most establishments to stop
              serving alcohol by 2 AM (3 AM in some downtown areas). Public
              transit (SkyTrain) generally ends around 1 AM, but NightBus
              services and ride-hailing options like Uber and Lyft are available.
              Tipping (15–20%) is customary in restaurants and cafés. It’s
              illegal to drink alcohol in public spaces (unless specifically
              designated). Also note that hours mentioned above may change, so
              it’s best to check official sites or call ahead if you’re craving
              something at 3 AM.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. How late do restaurants stay open in Vancouver?</h3>
              <p>
                While many close around 10 or 11 PM, you’ll find some that are
                open 24 hours (such as Duffin’s Donuts, Breka Bakery & Café,
                House of Dosas) or at least until the early morning hours on
                weekends. Always confirm specific hours, as they can vary.
              </p>

              <h3>2. Is Vancouver safe for late-night dining?</h3>
              <p>
                Generally, yes. Most late-night dining areas are in well-lit
                neighborhoods or downtown, and Vancouver is known for its
                relatively low crime rate. Still, use common sense, stick to main
                streets, and consider a taxi or rideshare if unsure.
              </p>

              <h3>3. Are there vegetarian or vegan late-night options?</h3>
              <p>
                Absolutely. The Naam is a fully vegetarian restaurant with
                extended hours, and many spots like Lucy’s or Breka offer
                vegetarian items. House of Dosas also has vegetarian-friendly
                options. Just check availability before heading out.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s late-night food scene offers something for every taste
              and budget, proving that the city still has delicious energy after
              midnight. From retro diners and donut shops to fusion hot dogs and
              upscale chophouses, these after-hours spots showcase the
              diversity, creativity, and warmth of Vancouver’s dining culture.
              Whether you’re ending a night out or just getting started, these
              eateries help you savor every hour in the city.
            </p>

            {/* SECTION: Disclaimers */}
            <h2 id="disclaimers">Disclaimers</h2>
            <p>
              Operating hours can change due to staffing or holidays; always
              confirm via the restaurant’s official website or by calling. If
              you have health or dietary concerns, contact establishments
              directly to ensure they can accommodate. Stay informed about local
              regulations by visiting official municipal or provincial sites.
              Enjoy responsibly and never drink and drive.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}